/**
 * RWBR — SAGLIANO'S ECONOMY, TRANSCRIBED.
 *
 * Every printed number from PART TWO of *A FICTIONAL GUIDE TO RED WHITE & BLUE
 * RACIN'* (Mike Sagliano, Version 2), pages 14–32, plus the two figures from
 * PART ONE the economy depends on. Nothing here is computed, interpolated or
 * tidied. Where the page and this file disagree, the page wins and this file is
 * the bug.
 *
 * Three numbers in this file are NOT what the page prints. Each is a locked
 * decision with its reasoning recorded, and each is flagged at its own table:
 *
 *   • DRIVER SALARY uses the p.31 ladder (40/80/160/320/640), not the p.26
 *     ladder (400/800/1600/3200/6400). D-010. The two tables are keyed
 *     identically and print all three periods; p.26 = p.31 × 10 in all fifteen
 *     cells. Only p.31 leaves every quality tier a positive operating margin
 *     against the sponsorship, automaker-support and race-cost tables that were
 *     tuned around it. The user's own workbook implements p.26 and 24 of its 46
 *     teams post a negative season.
 *
 *   • DEEP POCKETS reads 1000 at one quality, not the printed 10000. D-020.
 *     The remaining rows descend 800/600/400/200; a 1-quality owner worth
 *     twelve times a 5-quality owner inverts the table's own logic and is a
 *     dropped decimal.
 *
 *   • THE TV SPLIT is 65% track / 35% purse. D-011. The worked arithmetic on
 *     p.21 is unambiguous (35% of 1584 = 554.4, and 554 is the figure carried
 *     forward into the purse on p.23); the italic RASCAL flavour note on the
 *     same page says 65/25, which does not sum to 90. The flavour note is the
 *     error.
 *
 * SEASON LENGTH. Every yearly figure below is on Sagliano's 36-race baseline
 * (SAG/13), which is also our season length (D-004, D-012 — the playoff sits
 * inside the 36 rather than extending it, precisely so this file needs no
 * rescaling). `perRace()` prorates for any other length, per SAG/13: "You will
 * need to determine a weekly budget by prorating the yearly amounts for the
 * number of races in your own league."
 */

// ---------------------------------------------------------------------------
// Season baseline
// ---------------------------------------------------------------------------

/** SAG/13 — the baseline every yearly figure in this file is quoted against. */
export const SAGLIANO_BASELINE_RACES = 36;

/** Prorate a yearly figure to one race of an `n`-race season. */
export function perRace(yearlyMb: number, races: number = SAGLIANO_BASELINE_RACES): number {
  return yearlyMb / races;
}

/** Quality counts are 1–5 throughout Sagliano. Clamped, because a card can carry 0. */
export type QualityTier = 1 | 2 | 3 | 4 | 5;

export function qualityTier(count: number): QualityTier {
  return Math.min(5, Math.max(1, Math.round(count))) as QualityTier;
}

// ---------------------------------------------------------------------------
// INCOME — sponsorship (SAG/14)
// ---------------------------------------------------------------------------

export const CITE_SPONSORSHIP = 'SAG/14 — YEARLY SPONSORSHIP AMOUNTS (36-race baseline)';

export type SponsorTier = 'primary' | 'major' | 'associate';

/** SAG/14, verbatim. Yearly Mb on a 36-race season, by number of qualities. */
export const SPONSORSHIP_YEARLY: Record<SponsorTier, Record<QualityTier, number>> = {
  primary: { 1: 1000, 2: 1250, 3: 1500, 4: 1750, 5: 2000 },
  major: { 1: 200, 2: 277, 3: 350, 4: 422, 5: 500 },
  associate: { 1: 50, 2: 87, 3: 125, 4: 162, 5: 200 },
};

/**
 * SAG/15 — "A Driver can only have a maximum of one primary, two major, and
 * three associate sponsorships at any time in a season."
 */
export const SPONSOR_CAPS: Record<SponsorTier, number> = { primary: 1, major: 2, associate: 3 };

/** SAG/16 — the 2d6 that decides a new major or associate signing. */
export const CITE_SPONSOR_SIGNUP = 'SAG/15-16 — Signing Up New Major or Associate Team Sponsors';
export const SPONSOR_SIGNUP_SUCCEEDS_AT = 8; // "2-7 No · 8-12 Yes"

/**
 * SAG/17 — "Bad Mouthing the Sponsor". Rolled when a driver criticises his own
 * primary sponsor during Race Week (Y15 or Y53).
 */
export const CITE_BAD_MOUTHING = 'SAG/16-17 — Temporary Loss of Primary Sponsorship / "Bad Mouthing the Sponsor" Chart';

export type BadMouthOutcome = 'lose-a-month' | 'warned' | 'ignored';

export function badMouthOutcome(total2d6: number): BadMouthOutcome {
  if (total2d6 <= 5) return 'lose-a-month'; // 2-5
  if (total2d6 <= 10) return 'warned'; // 6-10
  return 'ignored'; // 11-12
}

/**
 * "Lose sponsorship for one month of races" (SAG/17). The guide never says how
 * many races a month is.
 *
 * RULING (D-029 §1): three. A 36-race season is a twelve-month calendar in every
 * league the guide describes, so a month is 36 ÷ 12. Configurable, because the
 * number is ours and not his.
 */
export const RACES_PER_MONTH = 3;

// ---------------------------------------------------------------------------
// INCOME — automaker support (SAG/25)
// ---------------------------------------------------------------------------

export const CITE_AUTOMAKER =
  'SAG/25 — AUTOMAKER SUPPORT: "Cash-strapped Teams will receive more support from the car companies than those Teams rolling in dough"';

/** Yearly Mb, 36-race season. Note it runs DOWN as qualities go up, by design. */
export const AUTOMAKER_SUPPORT_YEARLY: Record<QualityTier, number> = {
  1: 500,
  2: 400,
  3: 300,
  4: 200,
  5: 100,
};

// ---------------------------------------------------------------------------
// INCOME — family wealth (SAG/25)
// ---------------------------------------------------------------------------

export const CITE_FAMILY_WEALTH = 'SAG/25 — FAMILY WEALTH and The Deep Pockets Table';
export const CITE_DEEP_POCKETS_TYPO = 'D-020 — the printed 10000 at one quality is a dropped decimal; read 1000';

/** SAG/25 — 2,3,11,12 reach the Deep Pockets table; everything else finds nothing. */
export function hasDeepPockets(total2d6: number): boolean {
  return total2d6 === 2 || total2d6 === 3 || total2d6 === 11 || total2d6 === 12;
}

/** ⚠ 1-quality row is 1000 per D-020, not the printed 10000. */
export const DEEP_POCKETS: Record<QualityTier, number> = { 1: 1000, 2: 800, 3: 600, 4: 400, 5: 200 };

// ---------------------------------------------------------------------------
// INCOME — driver salary (SAG/31, per D-010)
// ---------------------------------------------------------------------------

export const CITE_SALARY = 'SAG/31 ROOKIE DRIVER SALARY CHART, ruled authoritative by D-010';
export const CITE_SALARY_CONTRADICTION =
  'D-010 — SAG/26 prints 400/800/1600/3200/6400 for the same row key; only SAG/31 leaves every tier a positive margin';

/** ⚠ The p.31 ladder (D-010). Yearly Mb on a 36-race season. */
export const DRIVER_SALARY_YEARLY: Record<QualityTier, number> = { 1: 40, 2: 80, 3: 160, 4: 320, 5: 640 };

/** SAG/27 — one bonus per season, on final Cup rank. */
export const CITE_BONUS = 'SAG/27 — BONUS, one per season for final Cup Standings rank';

export function seasonBonusMb(finalRank: number): number {
  if (finalRank === 1) return 20;
  if (finalRank <= 5) return 10;
  if (finalRank <= 10) return 5;
  if (finalRank <= 20) return 2.5;
  return 0; // 21st-43rd: None
}

// ---------------------------------------------------------------------------
// INCOME — royalties (SAG/27-28)
// ---------------------------------------------------------------------------

export const CITE_ROYALTIES =
  'SAG/27-28 — MERCHANDISE & ENDORSEMENT ROYALTIES: "ADD all four factors together and divide by 6. Then multiply the result by the driver\'s current salary."';
export const CITE_TEAM_ROYALTIES =
  'SAG/28 — "The Team\'s earn the same share of royalties as the Driver earns. This is not deducted from the Driver\'s share."';

export const ROYALTY_DIVISOR = 6;

/** SAG/27 — media presence, from Featured TV Driver frequency against the league average. */
export function royaltyMediaPoints(standing: 'above' | 'average' | 'below'): number {
  return standing === 'above' ? 3 : standing === 'below' ? -3 : 0;
}

/** SAG/27 — fan appeal, from positive vs negative Race Week image. */
export function royaltyFanPoints(standing: 'positive' | 'neutral' | 'negative'): number {
  return standing === 'positive' ? 2 : standing === 'negative' ? -2 : 0;
}

/** SAG/27 — talent is simply the quality count. */
export function royaltyTalentPoints(qualities: number): number {
  return qualityTier(qualities);
}

/** SAG/27 — performance, from the driver's position in the Cup Standings. */
export function royaltyPerformancePoints(finalRank: number): number {
  if (finalRank === 1) return 5;
  if (finalRank <= 5) return 4;
  if (finalRank <= 10) return 3;
  if (finalRank <= 30) return 2;
  return 1;
}

// ---------------------------------------------------------------------------
// INCOME — TV money (SAG/20-21)
// ---------------------------------------------------------------------------

export const CITE_TV_MONEY = 'SAG/20 — TV MONEY CHART, External Event (Fan) Factors';
export const CITE_TV_INTERNAL = 'SAG/21 — TV MONEY CHART, Internal Event (Race) Factors';
export const CITE_TV_SPLIT = 'SAG/21 + D-011 — league 10%, track keeps 65% of the remainder, 35% goes to the purse';

/** SAG/21 — "The base revenues … for a 36-race season totals 60,000 megabucks." */
export const TV_YEARLY_TOTAL_MB = 60_000;

/** SAG/20 — "Base revenue weekly is 1666 megabucks (for a 36-race season)." */
export const TV_BASE_REVENUE_MB = 1666;

export interface TvExternalRow {
  total: number;
  factor: string;
  /** Fractional adjustment to base revenue. 0 for the 6-8 "normal viewing" band. */
  pct: number;
}

/** SAG/20, verbatim including the flavour text — the booth reads these aloud. */
export const TV_EXTERNAL: Record<number, TvExternalRow> = {
  2: { total: 2, factor: 'Gloomy economy sours fans', pct: -0.25 },
  3: { total: 3, factor: 'Fan perception of too much TV coverage of sport', pct: -0.2 },
  4: { total: 4, factor: 'Other attractions (video games, social networks)', pct: -0.15 },
  5: { total: 5, factor: 'Dislike of league and its rules', pct: -0.1 },
  6: { total: 6, factor: 'Normal TV viewing', pct: 0 },
  7: { total: 7, factor: 'Normal TV viewing', pct: 0 },
  8: { total: 8, factor: 'Normal TV viewing', pct: 0 },
  9: { total: 9, factor: 'The league brand targeting new ethnic groups', pct: 0.1 },
  10: { total: 10, factor: 'Using social media to attract wider audience', pct: 0.15 },
  11: { total: 11, factor: 'Organizing college viewing parties', pct: 0.2 },
  12: { total: 12, factor: 'Rising excitement over races, drivers and League standings', pct: 0.25 },
};

/**
 * SAG/18 — the economy is judged by whether a 2 came up: "If 2 was rolled on the
 * TV Money or the Fan Attendance Chart, the economy is considered gloomy."
 */
export const GLOOMY_ECONOMY_ROLL = 2;

/** SAG/21 — the internal factors, each a percentage of the MODIFIED base revenue. */
export const TV_INTERNAL_PCT = {
  /** "Each female or one- or two-quality driver gets the pole (new faces)" */
  newFacePole: 0.02,
  /** "Each C or NONE Trophy driver placed in Top 5 (positions 2-5) starting positions" */
  newFaceTop5: 0.01,
  /** "An A or B Trophy driver gets the pole (same old faces)" */
  sameOldPole: -0.02,
  /** "Each A or B Trophy driver placed in other Top 5 (positions 2-5) starting positions" */
  sameOldTop5: -0.01,
  /** "2-5 drivers rolled for TV Week" */
  thinTvList: -0.02,
  /** "9-12 drivers rolled for TV Week" */
  fatTvList: 0.02,
  /** "Any Super Rare Event (from race last week)" */
  superRareLastWeek: 0.02,
  /** "Last lap (game turn) use of Surge or DUEL by driver to win the previous race" */
  lastTurnWin: 0.01,
  /** "Big One happened in last race" */
  bigOneLastRace: 0.03,
  /** "Altercation or fight during Race Week" */
  altercationThisWeek: 0.02,
} as const;

/** SAG/21 — the league's cut, off the top. */
export const TV_LEAGUE_SHARE = 0.1;
/** SAG/21 + D-011 — of what remains, the track keeps this and the rest is purse. */
export const TV_TRACK_SHARE = 0.65;
export const TV_PURSE_SHARE = 0.35;

// ---------------------------------------------------------------------------
// INCOME — fan attendance and the track purse (SAG/22)
// ---------------------------------------------------------------------------

export const CITE_ATTENDANCE = 'SAG/22 — FAN ATTENDANCE CHART';

export interface AttendanceRow {
  total: number;
  factor: string;
  /** Fraction of the track card's printed capacity. */
  pct: number;
}

export const ATTENDANCE: Record<number, AttendanceRow> = {
  2: { total: 2, factor: 'Gloomy economy (concern over high ticket and gas prices)', pct: 0.6 },
  3: { total: 3, factor: 'Fans watching on TV instead', pct: 0.65 },
  4: { total: 4, factor: 'Traffic and other inconveniences', pct: 0.7 },
  5: { total: 5, factor: "Fan discontent with league's organization alienates traditionalists", pct: 0.75 },
  6: { total: 6, factor: 'Event appealing to families', pct: 0.8 },
  7: { total: 7, factor: 'Event recaptures the younger generation', pct: 0.85 },
  8: { total: 8, factor: 'Fan excitement over drivers and league standings', pct: 0.9 },
  9: { total: 9, factor: 'Increase in TV commercials woos fans', pct: 0.95 },
  10: { total: 10, factor: 'Capacity crowd', pct: 1 },
  11: { total: 11, factor: 'Capacity crowd', pct: 1 },
  12: { total: 12, factor: 'Capacity crowd', pct: 1 },
};

/** SAG/22 — the four "other factors" added to the rolled percentage. */
export const ATTENDANCE_ADJUSTMENTS = {
  firstRaceOfYear: 0.1,
  /** "Other first 25% (rounding up) of all of the year's races". */
  earlySeason: 0.05,
  ongoingFeuds: 0.05,
  noFeuds: -0.05,
} as const;

/** SAG/22 — "Price of Ticket (reflects concession sales and parking costs) = .01 megabucks" ($100). */
export const TICKET_PRICE_MB = 0.01;

/** SAG/22 — "The track provides 20% for the Track Purse." */
export const TRACK_PURSE_SHARE = 0.2;

/**
 * Three of the 31 track cards print no capacity (the road courses). Sagliano's
 * chart is a percentage OF capacity, so it needs a number.
 *
 * RULING (D-029 §2): 60,000 — the median printed capacity of the short tracks
 * and road-adjacent venues in the set, and the smallest venue class the cards
 * describe. Flagged rather than hidden: a road-course purse computed off this
 * is an assumption, not a transcription.
 */
export const DEFAULT_TRACK_CAPACITY = 60_000;

// ---------------------------------------------------------------------------
// INCOME — the purse split (SAG/23)
// ---------------------------------------------------------------------------

export const CITE_PURSE = 'SAG/23 — THE DRIVER\'S AND THE TEAM\'S WINNINGS';
export const CITE_PURSE_SPLIT =
  'SAG/21 + D-011 — "The drivers compete for 40% of the purse in each race and the team owner gets 60%"';

/** SAG/23 — driver 40%, team 60%, of whatever the finishing position pays. */
export const DRIVER_PURSE_SHARE = 0.4;
export const TEAM_PURSE_SHARE = 0.6;

export interface PurseBand {
  /** Inclusive finishing positions. */
  from: number;
  to: number;
  /** Share of total winnings the whole band splits. */
  groupShare: number;
  label: string;
}

/**
 * SAG/23. The six bands sum to exactly 100% — 25 + 25 + 20 + 15 + 10 + 5 + 0
 * (D-011; I previously claimed they did not close, and I was wrong). The top
 * five taking half the purse is deliberate.
 */
export const PURSE_BANDS: readonly PurseBand[] = [
  { from: 1, to: 1, groupShare: 0.25, label: '1st' },
  { from: 2, to: 5, groupShare: 0.25, label: '2nd-5th' },
  { from: 6, to: 10, groupShare: 0.2, label: '6th-10th' },
  { from: 11, to: 20, groupShare: 0.15, label: '11th-20th' },
  { from: 21, to: 30, groupShare: 0.1, label: '21st-30th' },
  { from: 31, to: 40, groupShare: 0.05, label: '31st-40th' },
  { from: 41, to: 43, groupShare: 0, label: '41st-43rd' },
];

export function purseBandFor(place: number): PurseBand | undefined {
  return PURSE_BANDS.find((b) => place >= b.from && place <= b.to);
}

/**
 * A single finisher's share of the winnings, as a fraction.
 *
 * Sagliano prints both the group share and the per-driver share, and the two
 * agree only when every band is full: 25% ÷ 4 = 6.25%, 20% ÷ 5 = 4%, and so on.
 * A 41-car field leaves the last band short.
 *
 * RULING (D-029 §3): use the PRINTED PER-DRIVER share, not the group share
 * divided by the actual number of finishers in the band. Sagliano prints the
 * per-driver column as the operative figure, his worked example pays "43 each"
 * to 2nd-5th off a fixed divisor, and his own note — "Anything money left over?
 * The accountant pockets it" (SAG/24) — says plainly that the purse is not
 * expected to pay out in full. Dividing by the live count would instead
 * INCREASE a driver's cheque when the field is short, which is backwards.
 */
export function purseShareFor(place: number): number {
  const band = purseBandFor(place);
  if (!band) return 0;
  const slots = band.to - band.from + 1;
  return band.groupShare / slots;
}

// ---------------------------------------------------------------------------
// INCOME — contingency awards (SAG/24)
// ---------------------------------------------------------------------------

export const CITE_CONTINGENCY = 'SAG/24 — Contingency Sponsor Awards; "The drivers keep all contingency award money"';

/** SAG/24 — "Each contingency award for pole position and finish in the race is .5 megabucks." */
export const CONTINGENCY_AWARD_MB = 0.5;

/** SAG/24 — the finishing positions that carry a position award. The pole is separate. */
export const CONTINGENCY_FINISH_PLACES: readonly number[] = [2, 6, 11, 21, 31];

/** SAG/24 — "The Top Driver in Point Standings gets 1 megabuck (based on standings after the race.)" */
export const CONTINGENCY_POINTS_LEADER_MB = 1;

/** SAG/24 — last game turn, Surge or DUEL used to win the race. */
export const CONTINGENCY_LAST_TURN_WIN_MB = 0.5;

/**
 * SAG/24, verbatim on the sponsor names — flavour the booth and the post-race
 * summary can use directly. The number of awards and their conditions are
 * "entirely up to you"; these are the guide's own worked set.
 */
export const CONTINGENCY_SPONSORS: Record<string, { sponsor: string; tagline: string }> = {
  pole: { sponsor: 'Poler Bare Dancing Promotions', tagline: 'Drivers Can Get Poles too!' },
  '2': { sponsor: 'Flair Fashions', tagline: 'You can afford to dress up even as a runner-up.' },
  '6': { sponsor: 'Sixth Sense Business Solutions', tagline: 'Hypnotize Your Competitors' },
  '11': { sponsor: 'Eleventh Hour Mortuary', tagline: "Plan ahead. Don't be late for your own funeral." },
  '21': { sponsor: 'Wallop Whiskey', tagline: "If you're old enough to wreck a car, you're old enough to drink." },
  '31': {
    sponsor: 'Atomic Number 31 Metals',
    tagline: "A rare victory is like a rare metal. You'll find it like we did!",
  },
};

// ---------------------------------------------------------------------------
// EXPENSES — the weekly race cost (SAG/29)
// ---------------------------------------------------------------------------

export const CITE_RACE_COST = 'SAG/29 — WEEKLY RACE COST, 36 Mb per race run (1296 Mb per year on a 36-race season)';

/**
 * SAG/29, verbatim and itemised. "The costs on the chart are broken down for
 * information purposes only" — the total is the operative number, but the lines
 * are kept because D-007 carves the crew-investment budget out of the payroll
 * line and needs to name it.
 */
export const WEEKLY_RACE_COST_ITEMS: readonly { item: string; mb: number }[] = [
  { item: 'Engines', mb: 10 },
  { item: 'Radiators', mb: 1.8 },
  { item: 'Tires', mb: 3.2 },
  { item: 'Bits and Pieces', mb: 5 },
  { item: 'Brakes', mb: 2 },
  { item: 'Travel', mb: 3.2 },
  { item: 'Insurance', mb: 1 },
  { item: 'Per Diem', mb: 0.5 },
  { item: 'Payroll (Pit Crew, Mechanics, Engineers, Truck Driver)', mb: 8.5 },
  { item: 'League inspection fee', mb: 0.4 },
  { item: 'Race entry fee', mb: 0.4 },
];

/** SAG/29 — "Total costs (not including driver salary) 36 Mb per race run". */
export const WEEKLY_RACE_COST_MB = 36;

/** The payroll line D-007 draws crew investment from. */
export const PAYROLL_LINE_MB = 8.5;

// ---------------------------------------------------------------------------
// EXPENSES — damage (SAG/29-30)
// ---------------------------------------------------------------------------

export const CITE_CAR_REPLACEMENT = 'SAG/29 — "If a car is totally destroyed, it must be replaced." 15 Mb';
export const CITE_REPAIR_COST = 'SAG/30 — repair cost chart, rolled 2d6 in addition to the normal Weekly Race Cost';

export const CAR_REPLACEMENT_MB = 15;

/** SAG/30, re-indexed from the printed row order (2,12 / 3,11 / 5,9 / 6,7,8 / 4,10). */
export const REPAIR_COST_MB: Record<number, number> = {
  2: 15,
  3: 2.5,
  4: 10,
  5: 7.5,
  6: 5,
  7: 5,
  8: 5,
  9: 7.5,
  10: 10,
  11: 2.5,
  12: 15,
};

// ---------------------------------------------------------------------------
// EXPENSES — testing and R&D (SAG/30)
// ---------------------------------------------------------------------------

export const CITE_TESTING =
  'SAG/30 — "For every five races in a cup season, the Team engages in testing… The cost is 50 Mb for each test."';
export const CITE_RD = 'SAG/30 — RESEARCH & DEVELOPMENT: "For each race, each Team rolls 2d6"';
export const CITE_RD_SCALE =
  'D-030 — the printed R&D chart is quartered, because as printed it costs 72% of the entire weekly operating budget and puts 37 of 53 teams under water in a season';

/**
 * D-030 — HOWARD'S RULING ON THE ECONOMY, 15 Aug 2026.
 *
 * The measurement is in `OPEN_RULING_002_economy.md`. As printed, Sagliano's
 * economy does not balance: his four core tables are tuned to a designed margin
 * of +164/+274/+344/+334/+164 Mb a year, and R&D (935/yr expected) plus testing
 * (350/yr) were then added on top with nothing adjusted to pay for them.
 *
 * The ruling quarters R&D and leaves everything else exactly as printed. Across
 * three measured seasons that takes insolvency from 112 of 159 teams to 62, puts
 * a one-quality team at roughly break-even (+61 Mb) and a five-quality team at
 * +3,292 — so teams genuinely can fail, running well genuinely pays, and the
 * league survives.
 *
 * R&D is the line that moves because it is the one that does not survive
 * inspection: 25.97 Mb a race against a 36 Mb weekly budget that covers engines,
 * tires, brakes, travel, insurance, per diem, the whole crew payroll, and both
 * fees. Quartered it is 234 Mb a year — about 18% of operating cost, which reads
 * like a real racing budget.
 */
export const LEAGUE_R_AND_D_SCALE = 0.25;

export const TEST_COST_MB = 50;
export const RACES_PER_TEST = 5;

/** SAG/30. Note that BOTH 2 and 12 cost nothing — the chart is not monotone. */
export const RD_COST_MB: Record<number, number> = {
  2: 0,
  3: 20,
  4: 22.5,
  5: 25,
  6: 27.5,
  7: 27.5,
  8: 27.5,
  9: 30,
  10: 32.5,
  11: 35,
  12: 0,
};

/** Expected R&D per race under 2d6, for the solvency arithmetic. ≈25.97 Mb. */
export const RD_EXPECTED_MB = (() => {
  const w: Record<number, number> = { 2: 1, 3: 2, 4: 3, 5: 4, 6: 5, 7: 6, 8: 5, 9: 4, 10: 3, 11: 2, 12: 1 };
  let total = 0;
  for (const [k, weight] of Object.entries(w)) total += RD_COST_MB[Number(k)] * weight;
  return total / 36;
})();

// ---------------------------------------------------------------------------
// EXPENSES — fines (SAG/31-32)
// ---------------------------------------------------------------------------

export const CITE_DRIVER_FINES = 'SAG/31 — DRIVER FINES, paid by the driver to the League';
export const CITE_TEAM_FINES = 'SAG/32 — TEAM FINES, paid by the team to the League';

export interface FineRow {
  amountMb: number;
  offense: string;
  payer: 'driver' | 'team';
  /** Race Week chart id and code, in this project's own vocabulary. */
  triggers: { chart: string; code: string }[];
}

/**
 * SAG/31-32, keyed to Race Week chart codes. This is the join the cross-
 * reference sweep found: Sagliano prints fine amounts against die rolls but
 * never reproduces the charts, and the Race Week booklet prints the charts but
 * never prints an amount. Neither book is usable alone.
 *
 * THE ONE ILLEGIBLE CELL, RESOLVED (D-029 §9). SAG/32 keys a 5 Mb "major
 * violation of rules" to "rolls of 51, 34 on TV EVENT ( )" — the parentheses
 * were printed empty and a letter was written in afterwards by hand, read as an
 * X. Checking both codes against the charts settles it, and not in favour of a
 * single letter:
 *
 *   X 34  "Rules violation, PLACEMENT D."                        ✓ fined
 *   X 13  "Sanctions imposed for rules violation"                ✓ fined
 *   X 51  "Appearance on ESPN segment promoting race"            ✗ — and
 *         Sagliano scores this very row as a +2 MEDIA HYPE EVENT in his own
 *         sponsorship modifiers on p.15. He cannot have meant to fine a team
 *         5 Mb for the publicity he rewards them for two pages earlier.
 *   Y 51  "Team cited for rules violation during qualifying"     ✓ fined
 *   Y 14  the same sentence, word for word                       ✓ fined
 *
 * So "34" is the X row the handwriting names and "51" is a Y row — which is
 * presumably why the parentheses were left blank in the first place: two codes,
 * two charts, one pair of brackets. Y14 is fined alongside Y51 on the precedent
 * already set for Y43/Y63, which print Y13's offence identically and are fined
 * identically.
 */
export const FINES: readonly FineRow[] = [
  {
    amountMb: 20,
    offense: 'modification of car engine',
    payer: 'driver',
    triggers: [
      { chart: 'tv-event-z', code: '32' },
      { chart: 'tv-event-z', code: '52' },
    ],
  },
  {
    amountMb: 10,
    offense: 'other illegal modifications',
    payer: 'driver',
    triggers: [
      { chart: 'tv-event-z', code: '12' },
      { chart: 'tv-event-z', code: '42' },
    ],
  },
  {
    amountMb: 15,
    offense: 'altercation in pit area',
    payer: 'driver',
    triggers: [{ chart: 'tv-event-z', code: '15' }],
  },
  {
    amountMb: 10,
    offense: 'serious altercation',
    payer: 'driver',
    triggers: [{ chart: 'tv-event-y', code: '13' }],
  },
  {
    amountMb: 5,
    offense: 'moderate altercation',
    payer: 'driver',
    triggers: [{ chart: 'tv-event-x', code: '32' }],
  },
  {
    amountMb: 2.5,
    offense: 'minor altercation between drivers',
    payer: 'driver',
    triggers: [
      { chart: 'race-week-tv-list', code: '31' },
      { chart: 'race-week-tv-list', code: '42' },
    ],
  },
  {
    amountMb: 5,
    offense: 'major violation of rules',
    payer: 'team',
    triggers: [
      { chart: 'tv-event-x', code: '34' },
      { chart: 'tv-event-x', code: '13' },
      // D-029 §9 — Sagliano's "51" is a Y row, not an X row. See the note below.
      { chart: 'tv-event-y', code: '51' },
      { chart: 'tv-event-y', code: '14' },
    ],
  },
  {
    amountMb: 2.5,
    offense: 'minor rules violation',
    payer: 'team',
    triggers: [{ chart: 'race-week-tv-list', code: '62' }],
  },
];

/** The fine a chart row carries, if any. */
export function fineFor(chart: string, code: string): FineRow | undefined {
  return FINES.find((f) => f.triggers.some((t) => t.chart === chart && t.code === code));
}
